import React, { Fragment, useState } from 'react';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import PropTypes from 'prop-types';

import Alert from '../../../global/Alert';
import BoxedArrowLink from '../../../global/BoxedArrowLink/BoxedArrowLink';
import Button from '../../../global/Button';
import InputGroup from '../../../global/InputGroup';
import Spinner from '../../../global/Spinner';
import { getAsset } from '../../../../util/assets';
import { isValidEmail } from '../../../../util/email';
import { getErrorString } from '../../../../util/normalize-errors';
import { emailAuthenticationRequest as emailAuthenticationRequestMutation } from '../../../../graphql/mutations/account';

const propTypes = {
  backToPhoneAuth: PropTypes.func.isRequired,
  emailAuthenticationRequest: PropTypes.func.isRequired,
  next: PropTypes.string,
};

const EmailAuthLink = ({
  backToPhoneAuth,
  emailAuthenticationRequest,
  next = '/auth/password-setup?next=/app',
}) => {
  const [email, setEmail] = useState('');
  const [isEmailLinkSent, setEmailLinkSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendEmailError, setSendEmailError] = useState('');

  const sendEmailLink = async () => {
    setLoading(true);

    try {
      // We pass next (from the URL), so that the email link forwards them correctly.
      await emailAuthenticationRequest({
        email,
        next,
      });
      // Show user email sent screen.
      setEmailLinkSent(true);
    } catch (err) {
      setSendEmailError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      {isEmailLinkSent ? (
        <Fragment>
          <h4>Please verify your email</h4>
          <p>
            Striving for a secure experience with Waldo, we&apos;ve sent a link
            to verify your email.
          </p>
          <p>Please click on the link we&apos;ve sent to continue.</p>
          <h5>Didn&apos;t receive a verification link?</h5>
          <BoxedArrowLink onClick={sendEmailLink} className="mt-2">
            Re-send verification link
          </BoxedArrowLink>
        </Fragment>
      ) : (
        <form
          onSubmit={async event => {
            event.preventDefault();
            await sendEmailLink();
          }}
        >
          <p>
            Enter your email, and we&apos;ll send you a link that will sign you
            in.
          </p>
          <InputGroup
            addon={<img alt="" src={getAsset('/shutterbug/icons/email.svg')} />}
            id="email"
            name="email"
            onChange={event => setEmail(event.target.value)}
            placeholder="Email"
            required
            type="email"
            value={email}
            wrapperClassName="mb-2"
          />
          <Button type="submit" disabled={loading || !isValidEmail(email)}>
            {loading ? <Spinner light size="xs" /> : 'Continue'}
          </Button>
          <Button
            btnStyle="link"
            className="d-flex mt-3"
            onClick={backToPhoneAuth}
          >
            &larr;&nbsp;Back to phone verify
          </Button>
        </form>
      )}
      {sendEmailError && (
        <Alert
          type="danger"
          title="Sorry, there was a problem sending you an email."
        >
          {sendEmailError}
        </Alert>
      )}
    </Fragment>
  );
};

EmailAuthLink.propTypes = propTypes;

const withEmailAuthenticationMutation = graphql(
  emailAuthenticationRequestMutation,
  {
    props: ({ mutate }) => ({
      emailAuthenticationRequest: ({ email, next }) =>
        mutate({
          variables: {
            input: {
              email_address: email,
              intent: 'LOGIN',
              redirect_path: next,
            },
          },
        }),
    }),
  },
);

export default compose(withEmailAuthenticationMutation)(EmailAuthLink);
